/**
 * <XtsViewer3D> — Top-level React component.
 *
 * Thin wrapper around the r3f <Canvas>:
 *   - useNormalizedConfig:    pre-render validate + clamp pass
 *   - useSelectionState:      controlled-or-internal selection + click handlers
 *   - useXtsViewerHandle:     imperative ref API (camera, screenshots, etc.)
 *   - <XtsScene>:             everything inside the canvas (lights, modules,
 *                             movers, overlays)
 *
 * Pan/Zoom = OrbitControls defaults; Z-up; 1 unit = 1 mm.
 */

import React, {
  forwardRef,
  Suspense,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Canvas } from '@react-three/fiber';
import {
  OrthographicCamera,
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
} from 'three';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import { useNormalizedConfig } from './internals/useNormalizedConfig.js';
import { applyBeckhoffXtsConvention } from '../geometry/coordinateConvention.js';
import { useSelectionState } from './internals/useSelectionState.js';
import {
  useXtsViewerHandle,
  type XtsViewer3DRef,
} from './internals/useXtsViewerHandle.js';
import {
  MoverPositionStore,
  MoverPositionStoreContext,
} from './internals/MoverPositionStore.js';
import {
  ModuleStatusStore,
  ModuleStatusStoreContext,
} from './internals/ModuleStatusStore.js';
import { SidecarSourceContext } from '../assets/SidecarSourceContext.js';
import { XtsScene } from './internals/XtsScene.js';
import { CollisionMonitor } from './internals/CollisionMonitor.js';
import { ModuleCollisionMonitor } from './internals/ModuleCollisionMonitor.js';
import {
  resolveModuleHighlights,
  resolveToneMapping,
} from './internals/viewerHelpers.js';
import { silenceUpstreamThreeWarnings } from './internals/silenceThreeWarnings.js';
import type { ScreenshotOptions, ScreenshotResult } from './internals/screenshot.js';
import type {
  CheckMoverCollisionsOptions,
  MoverCollision,
} from '../geometry/moverCollision.js';
import type { ModuleCollision } from '../geometry/moduleCollision.js';
import type {
  AssetManifest,
  CameraProjection,
  CameraState,
  DisplayOptions,
  ModuleHighlight,
  ModuleRef,
  ModuleType3D,
  MoverRef,
  MoverToolType3D,
  MoverType3D,
  RailType3D,
  SelectionMode,
  SelectionState,
  StatorHeatmap,
  TrackTransform,
  XtsConfig,
  XtsViewerError,
} from '../types.js';
import type { OriginCorrection } from '../assets/SidecarLoader.js';
import type { HepcoGfxRailProfileDims } from '../geometry/HepcoGfxRailProfile.js';
import { JSDELIVR_ASSETS_BASE_URL } from '../assets/cdnUrl.js';
import { getSharedAssetLoader } from '../assets/AssetLoader.js';
import { setKtx2TranscoderUrl } from '../assets/configureGltfLoaders.js';

/**
 * Live overrides for module/mover/tool origin-correction sidecars. Used by
 * the calibration tool to preview adjustments without writing files.
 */
export interface SidecarOverrides {
  modules?: Partial<Record<ModuleType3D, OriginCorrection>>;
  movers?: Partial<Record<MoverType3D, OriginCorrection>>;
  tools?: Partial<Record<MoverToolType3D, OriginCorrection>>;
  rails?: Partial<Record<RailType3D, OriginCorrection>>;
}

export interface XtsViewer3DProps {
  /** Complete XTS configuration. */
  config: XtsConfig;
  /**
   * Base URL for GLB assets.
   *
   * Default: `JSDELIVR_ASSETS_BASE_URL`, which points to
   * `https://cdn.jsdelivr.net/npm/beckhoff-xts-viewer-3d-assets@<version>/models`
   * — the sister package with all GLBs, version-pinned to the
   * viewer version.
   */
  assetsBaseUrl?: string;
  /**
   * Source of the sidecar calibration data.
   *
   *   • `true` (default) — sidecars come from the bundled
   *     `BUILTIN_*_SIDECARS` constants in the JS library.
   *   • `false` — each sidecar is fetched live from
   *     `<assetsBaseUrl>/<id>.meta.json`.
   */
  useBuiltinSidecars?: boolean;
  /** Override of the module / mover asset manifest (for tests, alternative skins). */
  assetManifest?: AssetManifest;
  /** Display options. */
  display?: DisplayOptions;
  /** Initial camera. */
  initialCamera?: CameraState;
  /**
   * Live camera projection. Default `'perspective'`.
   *
   * `'orthographic'` (together with `topDown`, on by default) renders the flat
   * 2D plan view — straight down +Z, up = +Y — pixel-consistent with
   * `exportScreenshot({ mode: 'top-down' })`. Switching at runtime does NOT
   * recreate the WebGL context. In orthographic top-down, rotation is
   * auto-disabled (pan + zoom stay on) unless you set `lock.rotate` explicitly.
   */
  projection?: CameraProjection;
  /**
   * When `projection === 'orthographic'`, frame the scene straight from above.
   * Default `true`. (Only `true` is meaningful today; reserved for future
   * orthographic side/front views.)
   */
  topDown?: boolean;
  /** Lock interactions. */
  lock?: { rotate?: boolean; pan?: boolean; zoom?: boolean; selection?: boolean };
  /** Selection-Mode. Default: 'Off'. */
  selectionMode?: SelectionMode;
  /** Controlled selection. */
  selection?: SelectionState;
  /** Selection-Highlight-Farben. */
  selectionColors?: { module?: string; mover?: string };
  /**
   * Declarative module highlights — tint all modules matching the given
   * types with the specified color. Works independently of selection:
   * selection takes visual priority over highlights when both are active.
   *
   * Toggle on/off by setting to an array (enabled) or `undefined` / `[]`
   * (disabled). Multiple entries are supported; the first matching entry
   * per module type wins.
   *
   * @example
   * ```tsx
   * import { INFEED_MODULE_TYPES } from 'beckhoff-xts-viewer-3d';
   *
   * <XtsViewer3D
   *   moduleHighlights={showInfeed
   *     ? [{ moduleTypes: [...INFEED_MODULE_TYPES], color: '#FF6600' }]
   *     : undefined}
   * />
   * ```
   */
  moduleHighlights?: ModuleHighlight[];
  /**
   * Convenience boolean to highlight all infeed modules (Einspeisemodule)
   * without assembling a `moduleHighlights` entry yourself. Internally maps
   * to a `moduleHighlights` entry over `INFEED_MODULE_TYPES`.
   *
   * Combines with `moduleHighlights`: explicit entries keep priority on
   * overlapping module types (first matching entry wins).
   *
   * @example
   * ```tsx
   * <XtsViewer3D highlightInfeedModules infeedHighlightColor="#FF6600" />
   * ```
   */
  highlightInfeedModules?: boolean;
  /**
   * Color used when `highlightInfeedModules` is enabled. Ignored otherwise.
   * Default: `'#FF6600'`.
   */
  infeedHighlightColor?: string;
  /** Callbacks. */
  onSelectionChange?: (s: SelectionState) => void;
  onCameraChange?: (c: CameraState) => void;
  onMoverClick?: (m: MoverRef) => void;
  onModuleClick?: (m: ModuleRef) => void;
  onMoverHover?: (m: MoverRef | null) => void;
  onModuleHover?: (m: ModuleRef | null) => void;
  onError?: (err: XtsViewerError) => void;
  onAssetsLoaded?: () => void;
  /** Performance-Tuning. */
  performance?: {
    maxFps?: number;
    instancing?: boolean;
    autoPauseOnHidden?: boolean;
    /**
     * Enable demand rendering: frames only render when something changes
     * (camera move, mover position update, selection). Saves >90% GPU
     * time when the scene is idle. Default: true.
     */
    demandRendering?: boolean;
    /**
     * Cap the device pixel ratio. Values above 2 rarely improve
     * perceptible quality but double fill-rate cost on Retina/4K
     * displays. Default: 2.
     */
    maxDpr?: number;
    /**
     * Disable automatic per-frame shadow map updates. Shadows only
     * re-render on camera change. Default: true (when shadows enabled).
     */
    shadowOnDemand?: boolean;
    /**
     * Opt into the WebGPU renderer when the browser supports it.
     * Falls back to WebGL automatically. Default: false (experimental).
     */
    webgpu?: boolean;
    /**
     * Override the KTX2 (Basis) transcoder path used to decode compressed
     * textures in the GLB assets. Defaults to a CDN copy pinned to the
     * bundled three.js revision. Point this at a self-hosted
     * `basis_transcoder.{js,wasm}` directory for offline / air-gapped use.
     */
    ktx2TranscoderUrl?: string;
  };
  /**
   * Live override of the per-asset origin-correction sidecars. Useful for
   * the calibration tool.
   */
  sidecarOverrides?: SidecarOverrides;
  /**
   * Live override of per-XPU `trackTransform`. Keyed by `xpu.index`.
   */
  trackTransformOverrides?: Record<number, TrackTransform>;
  /**
   * Live override of the procedural Hepco-GFX rail profile dimensions.
   * Only the fields you supply are merged onto the defaults — useful for
   * the playground calibration UI / a sidecar-style `*.meta.json`.
   */
  hepcoGfxProfile?: Partial<HepcoGfxRailProfileDims>;
  /** CAD-style ViewCube overlay. */
  viewCube?: {
    enabled?: boolean;
    alignment?:
      | 'top-left'
      | 'top-right'
      | 'bottom-left'
      | 'bottom-right'
      | 'top-center'
      | 'bottom-center'
      | 'center-left'
      | 'center-right'
      | 'center-center';
    marginPx?: [number, number];
  };
  /** Stator heatmap data — coloured tube along each part's centerline. */
  statorHeatmap?: StatorHeatmap;
  /** Continuous mover collision monitoring. */
  collisionDetection?: {
    enabled?: boolean;
    warningGapMm?: number;
    intervalMs?: number;
    forceClosedLoop?: boolean;
    onCollisionsChange?: (collisions: MoverCollision[]) => void;
  };
  /**
   * Static MODULE overlap monitoring. Detects track modules that physically
   * overlap in 3D space — different XPUs / parts placed via `trackTransform`
   * / `orientation` / `partTransformation`, plus non-adjacent modules of the
   * same part. Adjacent (seam) modules and the closed-loop seam are
   * excluded. Recomputed on config / track-transform change (NOT per-frame,
   * since modules don't move at 60 Hz) — `onCollisionsChange` fires only
   * when the overlap set actually changes.
   */
  moduleCollisionDetection?: {
    enabled?: boolean;
    warningGapMm?: number;
    onCollisionsChange?: (collisions: ModuleCollision[]) => void;
  };
  /** Dev/Debug. */
  debug?: {
    showPathSplines?: boolean;
    showBoundingBoxes?: boolean;
    showOriginAxes?: boolean;
    showModuleLabels?: boolean;
    overlayFps?: boolean;
    showGrid?: boolean;
  };
  className?: string;
  style?: React.CSSProperties;
}

// Re-export the imperative ref API + screenshot types from their leaf modules
// so consumers can keep importing them from '../components/XtsViewer3D'.
export type { XtsViewer3DRef } from './internals/useXtsViewerHandle.js';
export type { ScreenshotOptions, ScreenshotResult };

const DEFAULT_ASSETS_BASE_URL = JSDELIVR_ASSETS_BASE_URL;

export const XtsViewer3D = forwardRef<XtsViewer3DRef, XtsViewer3DProps>(
  function XtsViewer3D(props, ref) {
    // Install the three.js console filter before <Canvas> mounts so the
    // upstream-only `THREE.Clock` deprecation warning from r3f's
    // root-store factory never reaches the consumer's console.
    // Idempotent — safe to call on every render.
    silenceUpstreamThreeWarnings();

    const {
      config,
      assetsBaseUrl = DEFAULT_ASSETS_BASE_URL,
      assetManifest,
      onError,
      initialCamera,
      lock,
      debug,
      className,
      style,
    } = props;

    const cameraRef = useRef<PerspectiveCamera | OrthographicCamera | null>(
      null,
    );
    const orbitRef = useRef<OrbitControlsImpl | null>(null);
    // Renderer + scene refs are captured in `<Canvas onCreated>` so the
    // imperative `exportScreenshot` method can render straight to an
    // offscreen target without re-creating the WebGL context.
    const glRef = useRef<WebGLRenderer | null>(null);
    const sceneRef = useRef<Scene | null>(null);
    // Imperative mover-position store. Same instance for the lifetime
    // of the component so `<XtsMover>` instances pick up writes without
    // having to re-render to grab a new context value.
    const moverStoreRef = useRef<MoverPositionStore>(
      undefined as unknown as MoverPositionStore,
    );
    if (!moverStoreRef.current) {
      moverStoreRef.current = new MoverPositionStore();
    }
    // Same pattern for module drive-status updates — out-of-React channel
    // so PLC-streamed alarm flags don't force a config re-render.
    const moduleStatusStoreRef = useRef<ModuleStatusStore>(
      undefined as unknown as ModuleStatusStore,
    );
    if (!moduleStatusStoreRef.current) {
      moduleStatusStoreRef.current = new ModuleStatusStore();
    }

    const rawNormalized = useNormalizedConfig(config, onError);
    const coordinateSystem = props.display?.coordinateSystem;
    const normalized = useMemo(
      () =>
        rawNormalized && coordinateSystem === 'beckhoff-xts'
          ? applyBeckhoffXtsConvention(rawNormalized)
          : rawNormalized,
      [rawNormalized, coordinateSystem],
    );

    // Merge the declarative `moduleHighlights` with the `highlightInfeedModules`
    // convenience boolean into the single array the renderer consumes.
    const effectiveModuleHighlights = useMemo(
      () =>
        resolveModuleHighlights(
          props.moduleHighlights,
          props.highlightInfeedModules,
          props.infeedHighlightColor,
        ),
      [
        props.moduleHighlights,
        props.highlightInfeedModules,
        props.infeedHighlightColor,
      ],
    );

    const {
      effectiveSelection,
      selectionMode,
      handleModuleClick,
      handleMoverClick,
      handlePointerMissed,
    } = useSelectionState({
      ...(props.selection !== undefined ? { selection: props.selection } : {}),
      selectionMode: props.selectionMode,
      selectionLocked: lock?.selection,
      ...(props.onSelectionChange ? { onSelectionChange: props.onSelectionChange } : {}),
      ...(props.onModuleClick ? { onModuleClick: props.onModuleClick } : {}),
      ...(props.onMoverClick ? { onMoverClick: props.onMoverClick } : {}),
    });
    void selectionMode; // captured inside the selection hook; reference here for clarity

    useXtsViewerHandle({
      ref,
      config,
      normalized,
      cameraRef,
      orbitRef,
      glRef,
      sceneRef,
      moverStoreRef,
      moduleStatusStoreRef,
      ...(props.trackTransformOverrides
        ? { trackTransformOverrides: props.trackTransformOverrides }
        : {}),
    });

    const initialCameraPosition: [number, number, number] =
      initialCamera?.positionMm ?? [1500, -1500, 1500];
    const initialCameraTarget: [number, number, number] =
      initialCamera?.targetMm ?? [0, 0, 0];

    const shadowsEnabled = props.display?.shadows === true;
    const environmentLightingEnabled =
      props.display?.environmentLighting !== false;
    const environmentIntensity = props.display?.environmentIntensity ?? 0.4;
    const toneMappingMode = resolveToneMapping(props.display?.toneMapping);
    const toneMappingExposure = props.display?.toneMappingExposure ?? 1;
    const demandRendering = props.performance?.demandRendering !== false;
    const maxDpr = props.performance?.maxDpr ?? 2;
    const shadowOnDemand =
      shadowsEnabled && props.performance?.shadowOnDemand !== false;

    useEffect(() => {
      const gl = glRef.current;
      if (!gl) return;
      gl.toneMapping = toneMappingMode;
      gl.toneMappingExposure = toneMappingExposure;
    }, [toneMappingMode, toneMappingExposure]);

    // Apply a custom KTX2 transcoder path before any GLB loads. The shared
    // KTX2Loader is built lazily on first load, so setting this here (ahead
    // of the Canvas mounting its children) is sufficient.
    const ktx2TranscoderUrl = props.performance?.ktx2TranscoderUrl;
    useEffect(() => {
      setKtx2TranscoderUrl(ktx2TranscoderUrl ?? null);
    }, [ktx2TranscoderUrl]);

    return (
      <div className={className} style={{ width: '100%', height: '100%', ...style }}>
        <Canvas
          camera={{
            fov: 45,
            near: 1,
            far: 50_000,
            position: initialCameraPosition,
            up: [0, 0, 1],
          }}
          gl={{
            alpha: true,
            antialias: true,
            premultipliedAlpha: true,
            powerPreference: 'high-performance',
          }}
          dpr={[1, maxDpr]}
          frameloop={demandRendering ? 'demand' : 'always'}
          shadows={shadowsEnabled ? 'soft' : false}
          onPointerMissed={handlePointerMissed}
          onCreated={({ camera, controls, scene, gl }) => {
            cameraRef.current = camera as PerspectiveCamera;
            if (controls) orbitRef.current = controls as OrbitControlsImpl;
            glRef.current = gl as WebGLRenderer;
            sceneRef.current = scene as Scene;
            camera.lookAt(...initialCameraTarget);
            (gl as WebGLRenderer).toneMapping = toneMappingMode;
            (gl as WebGLRenderer).toneMappingExposure = toneMappingExposure;
            scene.background = null;
            gl.setClearAlpha(0);
            // Wire compression decoders onto the public preload loader now
            // that the renderer exists (KTX2 needs it for GPU-format
            // detection). The render hot-path is wired separately via
            // useGltfClone's extendLoader.
            getSharedAssetLoader().configure({
              gl: gl as WebGLRenderer,
              ...(ktx2TranscoderUrl ? { transcoderUrl: ktx2TranscoderUrl } : {}),
            });
          }}
        >
          <MoverPositionStoreContext.Provider value={moverStoreRef.current}>
           <ModuleStatusStoreContext.Provider value={moduleStatusStoreRef.current}>
            <SidecarSourceContext.Provider
              value={{ useBuiltins: props.useBuiltinSidecars !== false }}
            >
              <Suspense fallback={null}>
                {normalized ? (
                  <XtsScene
                    config={normalized}
                    assetsBaseUrl={assetsBaseUrl}
                    {...(assetManifest ? { assetManifest } : {})}
                    showGrid={debug?.showGrid !== false}
                    shadows={shadowsEnabled}
                    shadowOnDemand={shadowOnDemand}
                    demandRendering={demandRendering}
                    environmentLighting={environmentLightingEnabled}
                    environmentIntensity={environmentIntensity}
                    overlayFps={debug?.overlayFps === true}
                    {...(props.viewCube ? { viewCube: props.viewCube } : {})}
                    {...(props.display ? { display: props.display } : {})}
                    selection={effectiveSelection}
                    {...(props.selectionColors
                      ? { selectionColors: props.selectionColors }
                      : {})}
                    {...(effectiveModuleHighlights
                      ? { moduleHighlights: effectiveModuleHighlights }
                      : {})}
                    onModuleClick={handleModuleClick}
                    onMoverClick={handleMoverClick}
                    {...(props.performance
                      ? { performance: props.performance }
                      : {})}
                    {...(props.sidecarOverrides
                      ? { sidecarOverrides: props.sidecarOverrides }
                      : {})}
                    {...(props.trackTransformOverrides
                      ? { trackTransformOverrides: props.trackTransformOverrides }
                      : {})}
                    {...(props.hepcoGfxProfile
                      ? { hepcoGfxProfile: props.hepcoGfxProfile }
                      : {})}
                    {...(props.statorHeatmap
                      ? { statorHeatmap: props.statorHeatmap }
                      : {})}
                    onOrbitReady={(c) => {
                      orbitRef.current = c;
                    }}
                    onCameraReady={(c) => {
                      cameraRef.current = c;
                    }}
                    projection={props.projection ?? 'perspective'}
                    topDown={props.topDown !== false}
                    initialCameraPosition={initialCameraPosition}
                    {...(lock ? { lock } : {})}
                  />
                ) : null}
                {normalized && props.collisionDetection?.enabled && (
                  <CollisionMonitor
                    config={normalized}
                    {...(props.collisionDetection.warningGapMm !== undefined
                      ? { warningGapMm: props.collisionDetection.warningGapMm }
                      : {})}
                    {...(props.collisionDetection.intervalMs !== undefined
                      ? { intervalMs: props.collisionDetection.intervalMs }
                      : {})}
                    {...(props.collisionDetection.forceClosedLoop !== undefined
                      ? {
                          forceClosedLoop:
                            props.collisionDetection.forceClosedLoop,
                        }
                      : {})}
                    onCollisionsChange={
                      props.collisionDetection.onCollisionsChange ?? (() => {})
                    }
                  />
                )}
                {normalized && props.moduleCollisionDetection?.enabled && (
                  <ModuleCollisionMonitor
                    config={normalized}
                    {...(props.config.orientation
                      ? { orientation: props.config.orientation }
                      : {})}
                    {...(props.trackTransformOverrides
                      ? {
                          trackTransformOverrides:
                            props.trackTransformOverrides,
                        }
                      : {})}
                    {...(props.moduleCollisionDetection.warningGapMm !==
                    undefined
                      ? {
                          warningGapMm:
                            props.moduleCollisionDetection.warningGapMm,
                        }
                      : {})}
                    onCollisionsChange={
                      props.moduleCollisionDetection.onCollisionsChange ??
                      (() => {})
                    }
                  />
                )}
              </Suspense>
            </SidecarSourceContext.Provider>
           </ModuleStatusStoreContext.Provider>
          </MoverPositionStoreContext.Provider>
        </Canvas>
      </div>
    );
  },
);
